import { z } from "zod";

import { faultTypeSchema, shortCircuitCaseSchema } from "./faultContract";
import { prospectiveCurrentSourceSchema } from "./starterCoordinationContract";

// The controlled vocabularies of the LV breaker duty and selectivity module
// (EOS-05b, Master Prompt A21), copied verbatim from the backend so a value the
// API can send is never rejected here. Sources at commit 47a441f:
// backend/app/domain/electrical/breaker_selectivity/results.py
// (BreakerSelectivityStatus, DeviceRole, BreakerSelectivityWarningCode and the
// five code sets) and .../breaker_selectivity/models.py (BreakerKind, DutyBasis,
// SelectivityKind). breakerSelectivityContract.test.ts pins each list.
//
// The prospective current source, the calculation case and the fault type of the
// linked fault run are shared enums (fault_link.py and the fault module), so they
// are reused from the starter coordination and fault contracts rather than written
// out a second time.

export const breakerSelectivityStatusSchema = z.enum([
  "VALID",
  "WARNING",
  "REVIEW_REQUIRED",
  "NO_SOLUTION",
]);

export const breakerSelectivityWarningCodeSchema = z.enum([
  "ICU_BELOW_PROSPECTIVE_CURRENT",
  "ICS_BELOW_PROSPECTIVE_CURRENT",
  "ICM_BELOW_PEAK_CURRENT",
  "RELIES_ON_CASCADING",
  "MAKING_CHECK_NOT_EVALUATED",
  "RATED_VOLTAGE_BELOW_SYSTEM",
  "SELECTIVITY_EVIDENCE_NOT_ESTABLISHED",
  "SELECTIVITY_NOT_MET",
  "SELECTIVITY_PARTIAL_NOT_REQUIRED",
  "PROSPECTIVE_CURRENT_NOT_LINKED",
  "FAULT_RUN_NOT_MAXIMUM_THREE_PHASE",
  "FAULT_RUN_BUS_MISMATCH",
  "BREAKER_EVIDENCE_MANUFACTURER_DECLARED",
]);

export const breakerKindSchema = z.enum(["ACB", "MCCB", "MCB"]);

export const dutyBasisSchema = z.enum(["ULTIMATE", "SERVICE"]);

export const selectivityKindSchema = z.enum(["TOTAL", "PARTIAL"]);

export const deviceRoleSchema = z.enum(["INCOMER", "OUTGOING"]);

export { faultTypeSchema, prospectiveCurrentSourceSchema, shortCircuitCaseSchema };

/** A failed check: the device as rated cannot be accepted at this fault level (A21 (b), (d)). */
export const NO_SOLUTION_WARNING_CODES = [
  "ICU_BELOW_PROSPECTIVE_CURRENT",
  "ICS_BELOW_PROSPECTIVE_CURRENT",
  "ICM_BELOW_PEAK_CURRENT",
  "SELECTIVITY_NOT_MET",
] as const;

/**
 * Evidence the engineer still owes: a device that relies on declared cascading (never a
 * pass), a making check without a peak current, a rated voltage below the system voltage
 * (IR-001 F1), no selectivity evidence, or a fault level that is not linked to a maximum
 * three-phase fault run of the study's own bus (A21 (a)-(d)).
 */
export const REVIEW_REQUIRED_WARNING_CODES = [
  "RELIES_ON_CASCADING",
  "MAKING_CHECK_NOT_EVALUATED",
  "RATED_VOLTAGE_BELOW_SYSTEM",
  "SELECTIVITY_EVIDENCE_NOT_ESTABLISHED",
  "PROSPECTIVE_CURRENT_NOT_LINKED",
  "FAULT_RUN_NOT_MAXIMUM_THREE_PHASE",
  "FAULT_RUN_BUS_MISMATCH",
] as const;

/** The incomer has no cascading or selectivity declaration: its codes are the duty codes. */
export const INCOMER_WARNING_CODES = [
  "ICU_BELOW_PROSPECTIVE_CURRENT",
  "ICS_BELOW_PROSPECTIVE_CURRENT",
  "ICM_BELOW_PEAK_CURRENT",
  "MAKING_CHECK_NOT_EVALUATED",
  "RATED_VOLTAGE_BELOW_SYSTEM",
] as const;

/** The codes an outgoing device may carry: the duty codes plus its declarations. */
export const OUTGOING_WARNING_CODES = [
  ...INCOMER_WARNING_CODES,
  "RELIES_ON_CASCADING",
  "SELECTIVITY_EVIDENCE_NOT_ESTABLISHED",
  "SELECTIVITY_NOT_MET",
  "SELECTIVITY_PARTIAL_NOT_REQUIRED",
] as const;

/** The codes any device may carry (the outgoing set holds the incomer's). */
export const DEVICE_WARNING_CODES = OUTGOING_WARNING_CODES;

/** The codes the study itself may carry. */
export const STUDY_WARNING_CODES = [
  "PROSPECTIVE_CURRENT_NOT_LINKED",
  "FAULT_RUN_NOT_MAXIMUM_THREE_PHASE",
  "FAULT_RUN_BUS_MISMATCH",
  "BREAKER_EVIDENCE_MANUFACTURER_DECLARED",
] as const;

/**
 * Carried by every result (A21 (g), GAP-021): duty, selectivity and cascading are
 * checked as manufacturer-declared evidence, never proven by the engine. It gives
 * WARNING, never REVIEW_REQUIRED.
 */
export const BREAKER_EVIDENCE_MANUFACTURER_DECLARED = "BREAKER_EVIDENCE_MANUFACTURER_DECLARED";

export type BreakerSelectivityStatus = z.infer<typeof breakerSelectivityStatusSchema>;
export type BreakerSelectivityWarningCode = z.infer<typeof breakerSelectivityWarningCodeSchema>;
export type BreakerKind = z.infer<typeof breakerKindSchema>;
export type DutyBasis = z.infer<typeof dutyBasisSchema>;
export type SelectivityKind = z.infer<typeof selectivityKindSchema>;
export type DeviceRole = z.infer<typeof deviceRoleSchema>;
